import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import '../styles/WorldBuilder.css';

const API = process.env.REACT_APP_SERVER_URL || '';
const TILE_BASE = '/tiles/';

// ============================================================
// WORLD BUILDER  Main Component
// ============================================================
const WorldBuilder = ({ onBack }) => {
  const [view, setView] = useState('world'); // 'world' | 'editor' | 'list'
  const [editingMap, setEditingMap] = useState(null);
  const [worldData, setWorldData] = useState(null);
  const [mazeList, setMazeList] = useState([]);
  const [tileList, setTileList] = useState([]);
  const [statusMsg, setStatusMsg] = useState('');
  const [newMapDialog, setNewMapDialog] = useState(null); // { adjacentMap, direction } or {}
  const [generatingMap, setGeneratingMap] = useState(false);

  const showStatus = (msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(''), 4000);
  };

  const fetchWorld = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/worldbuilder/world`);
      setWorldData(res.data);
    } catch (err) {
      console.error('Failed to load world:', err);
    }
  }, []);

  const fetchMazeList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/worldbuilder/mazes`);
      setMazeList(res.data);
    } catch (err) {
      console.error('Failed to load maze list:', err);
    }
  }, []);

  const fetchTiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/worldbuilder/tiles`);
      setTileList(res.data);
    } catch (err) {
      console.error('Failed to load tiles:', err);
    }
  }, []);

  const generateWorldMap = useCallback(async (size = 8) => {
    setGeneratingMap(true);
    showStatus('Generating world map...');
    try {
      const res = await axios.get(`${API}/api/worldbuilder/worldmap?size=${size}`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      showStatus('World map opened in new tab!');
    } catch (err) {
      console.error('Failed to generate world map:', err);
      showStatus('Failed to generate world map');
    } finally {
      setGeneratingMap(false);
    }
  }, []);

  useEffect(() => {
    fetchTiles();
    fetchMazeList();
    fetchWorld();
  }, [fetchTiles, fetchMazeList, fetchWorld]);

  const openMap = async (filename) => {
    try {
      const res = await axios.get(`${API}/api/worldbuilder/maze/${filename}`);
      const data = res.data;
      // Validate grid data to prevent render crashes
      if (data.grid && Array.isArray(data.grid)) {
        for (let r = 0; r < data.grid.length; r++) {
          if (!Array.isArray(data.grid[r])) data.grid[r] = [];
          for (let c = 0; c < (data.cols || 0); c++) {
            if (!data.grid[r][c]) {
              data.grid[r][c] = { t: 'grass1.jpg', p: false, l: null, s: null, nb: false, c: null, bb: false };
            }
          }
        }
      }
      setEditingMap(data);
      setView('editor');
    } catch (err) {
      console.error('Failed to open map:', err);
      showStatus(`Failed to open ${filename}`);
    }
  };

  const handleCreateMap = async (filename, rows, cols, defaultTile, music, connectTo) => {
    try {
      await axios.post(`${API}/api/worldbuilder/maze`, {
        filename, rows, cols, defaultTile, music, connectTo
      });
      showStatus(`Created ${filename}`);
      setNewMapDialog(null);
      fetchMazeList();
      fetchWorld();
      openMap(filename);
    } catch (err) {
      showStatus(`${err.response?.data?.error || 'Failed to create map'}`);
    }
  };

  const handleSaveMap = async (mapData) => {
    try {
      await axios.put(`${API}/api/worldbuilder/maze/${mapData.filename}`, {
        grid: mapData.grid,
        rows: mapData.rows,
        cols: mapData.cols,
        music: mapData.music,
        monsters: mapData.monsters,
        convo: mapData.convo
      });
      showStatus(`💾 Saved ${mapData.filename}`);
      fetchWorld();
    } catch (err) {
      showStatus(`Failed to save: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeleteMap = async (filename) => {
    if (!window.confirm(`Permanently delete ${filename}?\n\nThis cannot be undone!`)) return;
    try {
      await axios.delete(`${API}/api/worldbuilder/maze/${filename}`);
      showStatus(`🗑️ Deleted ${filename}`);
      setEditingMap(null);
      setView('world');
      fetchMazeList();
      fetchWorld();
    } catch (err) {
      showStatus(`Failed to delete: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleCloneMap = async (srcFilename) => {
    const baseName = srcFilename.replace('.dat', '');
    const newName = prompt('Enter filename for the copy:', `${baseName}_copy.dat`);
    if (!newName) return;
    const fn = newName.endsWith('.dat') ? newName : newName + '.dat';
    try {
      await axios.post(`${API}/api/worldbuilder/maze/${srcFilename}/clone`, { newFilename: fn });
      showStatus(`Cloned  ${fn}`);
      fetchMazeList();
      fetchWorld();
      openMap(fn);
    } catch (err) {
      showStatus(`${err.response?.data?.error || 'Failed to clone'}`);
    }
  };

  const goBackToWorld = () => {
    setView('world');
    setEditingMap(null);
    fetchWorld();
  };

  return (
    <div className="wb-container">
      {/* Header */}
      <div className="wb-header">
        <div className="wb-header-left">
          <button className="wb-btn wb-btn-back" onClick={onBack}>🛠️ Admin</button>
          <h1>World Builder</h1>
          {view === 'editor' && editingMap && (
            <span className="wb-editing-label">Editing: {editingMap.filename}</span>
          )}
        </div>
        <div className="wb-header-right">
          {statusMsg && <span className="wb-status-msg">{statusMsg}</span>}
          <button className={`wb-btn ${view === 'world' ? 'active' : ''}`} onClick={() => { setView('world'); setEditingMap(null); }}>
             World View
          </button>
          <button className={`wb-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
             Map List
          </button>
          <button className="wb-btn wb-btn-success" onClick={() => setNewMapDialog({})}>
            ✨ New Map
          </button>
          <button
            className="wb-btn"
            onClick={() => generateWorldMap(8)}
            disabled={generatingMap}
            title="Generate a full tile-level world map in a new tab"
          >
            {generatingMap ? '⏳ Generating...' : '🗺️ World Map'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="wb-content">
        {view === 'world' && (
          <WorldOverview
            worldData={worldData}
            onOpenMap={openMap}
            onNewMapEdge={(adj, dir) => setNewMapDialog({ adjacentMap: adj, direction: dir })}
          />
        )}
        {view === 'editor' && editingMap && (
          <MapEditor
            mapData={editingMap}
            tileList={tileList}
            mazeList={mazeList}
            onSave={handleSaveMap}
            onDelete={handleDeleteMap}
            onClone={handleCloneMap}
            onBack={goBackToWorld}
            showStatus={showStatus}
          />
        )}
        {view === 'list' && (
          <MapList mazeList={mazeList} onOpenMap={openMap} onRefresh={fetchMazeList} onDelete={handleDeleteMap} onClone={handleCloneMap} />
        )}
      </div>

      {/* New Map Dialog */}
      {newMapDialog && (
        <NewMapDialog
          connectTo={newMapDialog.adjacentMap ? newMapDialog : null}
          onClose={() => setNewMapDialog(null)}
          onCreate={handleCreateMap}
        />
      )}
    </div>
  );
};

// ============================================================
// WORLD OVERVIEW  Bird's-eye grid of all maps
// ============================================================
const WorldOverview = ({ worldData, onOpenMap, onNewMapEdge }) => {
  const containerRef = useRef(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [zoom, setZoom] = useState(1);

  if (!worldData) return <div className="wb-loading">Loading world data...</div>;

  const { grid, width, height } = worldData;

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom(z => Math.max(0.3, Math.min(3, z + (e.deltaY > 0 ? -0.1 : 0.1))));
    }
  };

  // Build clickable edge slots (empty neighbors of existing maps)
  const edgeSlots = new Set();
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (grid[y][x]) {
        // Check all 4 neighbors
        const dirs = [
          { dx: 0, dy: -1, dir: 'south' },  // cell above  new map's south connects
          { dx: 0, dy: 1, dir: 'north' },
          { dx: -1, dy: 0, dir: 'east' },
          { dx: 1, dy: 0, dir: 'west' }
        ];
        for (const { dx, dy, dir } of dirs) {
          const nx = x + dx;
          const ny = y + dy;
          if (ny >= 0 && ny < height && nx >= 0 && nx < width && !grid[ny][nx]) {
            edgeSlots.add(`${nx},${ny},${grid[y][x].filename},${dir}`);
          }
          // Also allow expanding outside current bounds
          if (ny < 0 || ny >= height || nx < 0 || nx >= width) {
            edgeSlots.add(`${nx},${ny},${grid[y][x].filename},${dir}`);
          }
        }
      }
    }
  }

  // Expand grid to include edge slots
  let minX = 0, maxX = width - 1, minY = 0, maxY = height - 1;
  for (const slot of edgeSlots) {
    const [sx, sy] = slot.split(',').map(Number);
    minX = Math.min(minX, sx);
    maxX = Math.max(maxX, sx);
    minY = Math.min(minY, sy);
    maxY = Math.max(maxY, sy);
  }
  const totalW = maxX - minX + 1;
  const totalH = maxY - minY + 1;

  return (
    <div className="wb-world" ref={containerRef} onWheel={handleWheel}>
      <div className="wb-world-info">
        <span>{worldData.totalMaps} maps placed  {worldData.unmappedMaps} unmapped  Ctrl+Scroll to zoom</span>
        <div className="wb-zoom-controls">
          <button className="wb-btn wb-btn-xs" onClick={() => setZoom(z => Math.max(0.3, z - 0.2))}>✖</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button className="wb-btn wb-btn-xs" onClick={() => setZoom(z => Math.min(3, z + 0.2))}>+</button>
          <button className="wb-btn wb-btn-xs" onClick={() => setZoom(1)}>Reset</button>
        </div>
      </div>
      <div className="wb-world-scroll">
        <div
          className="wb-world-grid"
          style={{
            gridTemplateColumns: `repeat(${totalW}, ${60 * zoom}px)`,
            gridTemplateRows: `repeat(${totalH}, ${60 * zoom}px)`,
          }}
        >
          {Array.from({ length: totalH }, (_, gy) =>
            Array.from({ length: totalW }, (_, gx) => {
              const origX = gx + minX;
              const origY = gy + minY;
              const inBounds = origY >= 0 && origY < height && origX >= 0 && origX < width;
              const cell = inBounds ? grid[origY][origX] : null;
              const isHovered = hoveredCell === `${gx},${gy}`;

              // Check if this is an edge slot
              let edgeInfo = null;
              if (!cell) {
                for (const slot of edgeSlots) {
                  const parts = slot.split(',');
                  if (parseInt(parts[0]) === origX && parseInt(parts[1]) === origY) {
                    edgeInfo = { adjacentMap: parts[2], direction: parts[3] };
                    break;
                  }
                }
              }

              if (cell) {
                return (
                  <div
                    key={`${gx}-${gy}`}
                    className={`wb-world-cell filled ${isHovered ? 'hovered' : ''}`}
                    onMouseEnter={() => setHoveredCell(`${gx},${gy}`)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => onOpenMap(cell.filename)}
                    title={`${cell.filename} (${cell.rows}${cell.cols})`}
                  >
                    <span className="wb-world-cell-name" style={{ fontSize: `${Math.max(7, 9 * zoom)}px` }}>
                      {cell.filename.replace('.dat', '')}
                    </span>
                  </div>
                );
              } else if (edgeInfo) {
                return (
                  <div
                    key={`${gx}-${gy}`}
                    className="wb-world-cell edge-slot"
                    onClick={() => onNewMapEdge(edgeInfo.adjacentMap, edgeInfo.direction)}
                    title={`Click to create new map (connects ${edgeInfo.direction} of ${edgeInfo.adjacentMap})`}
                  >
                    <span className="wb-edge-plus">+</span>
                  </div>
                );
              } else {
                return <div key={`${gx}-${gy}`} className="wb-world-cell empty" />;
              }
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAP LIST 🔍 Searchable list of all maps
// ============================================================
const MapList = ({ mazeList, onOpenMap, onRefresh, onDelete, onClone }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, maps, shops

  const filtered = mazeList.filter(m => {
    if (filter === 'maps' && m.isShop) return false;
    if (filter === 'shops' && !m.isShop) return false;
    return m.filename.toLowerCase().includes(search.toLowerCase()) ||
      (m.shopName && m.shopName.toLowerCase().includes(search.toLowerCase()));
  });

  return (
    <div className="wb-list">
      <div className="wb-list-controls">
        <input
          type="text"
          placeholder="Search maps..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="wb-input wb-input-search"
        />
        <select value={filter} onChange={e => setFilter(e.target.value)} className="wb-select">
          <option value="all">All ({mazeList.length})</option>
          <option value="maps">Maps ({mazeList.filter(m => !m.isShop).length})</option>
          <option value="shops">Shops ({mazeList.filter(m => m.isShop).length})</option>
        </select>
        <button className="wb-btn wb-btn-sm" onClick={onRefresh}></button>
      </div>
      <div className="wb-list-grid">
        {filtered.map(m => (
          <div key={m.filename} className={`wb-list-card ${m.isShop ? 'shop' : ''}`} onClick={() => onOpenMap(m.filename)}>
            <div className="wb-list-card-actions">
              <button className="wb-list-card-action" onClick={(e) => { e.stopPropagation(); onClone(m.filename); }} title="Clone map"></button>
              <button className="wb-list-card-action wb-list-card-action-danger" onClick={(e) => { e.stopPropagation(); onDelete(m.filename); }} title="Delete map">✖</button>
            </div>
            <div className="wb-list-card-name">{m.filename.replace('.dat', '')}</div>
            <div className="wb-list-card-info">
              {m.isShop ? (
                <span>{m.shopName}</span>
              ) : (
                <>
                  <span>{m.rows}{m.cols}</span>
                  {m.music && <span></span>}
                  {m.monsterCount > 0 && <span>{m.monsterCount}</span>}
                  {m.hasConvo && <span></span>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// NEW MAP DIALOG
// ============================================================
const NewMapDialog = ({ connectTo, onClose, onCreate }) => {
  const [filename, setFilename] = useState('');
  const [rows, setRows] = useState(10);
  const [cols, setCols] = useState(10);
  const [defaultTile, setDefaultTile] = useState('grass1.jpg');
  const [music, setMusic] = useState('');

  const handleCreate = () => {
    const fn = filename.endsWith('.dat') ? filename : filename + '.dat';
    onCreate(fn, rows, cols, defaultTile, music, connectTo);
  };

  return (
    <div className="wb-modal-overlay" onClick={onClose}>
      <div className="wb-modal" onClick={e => e.stopPropagation()}>
        <h3>✨ New Map</h3>
        {connectTo && (
          <p className="wb-connect-info">
             Will auto-connect {connectTo.direction} of <strong>{connectTo.adjacentMap}</strong>
          </p>
        )}
        <div className="wb-form-row">
          <label>Filename:</label>
          <input type="text" value={filename} onChange={e => setFilename(e.target.value)} placeholder="mymap.dat" className="wb-input" autoFocus />
        </div>
        <div className="wb-form-row">
          <label>Size:</label>
          <input type="number" value={rows} onChange={e => setRows(parseInt(e.target.value) || 10)} min="3" max="50" className="wb-input wb-input-sm" />
          <span></span>
          <input type="number" value={cols} onChange={e => setCols(parseInt(e.target.value) || 10)} min="3" max="50" className="wb-input wb-input-sm" />
        </div>
        <div className="wb-form-row">
          <label>Default Tile:</label>
          <input type="text" value={defaultTile} onChange={e => setDefaultTile(e.target.value)} className="wb-input" />
        </div>
        <div className="wb-form-row">
          <label>Music:</label>
          <input type="text" value={music} onChange={e => setMusic(e.target.value)} placeholder="plains.mid" className="wb-input" />
        </div>
        <div className="wb-modal-buttons">
          <button className="wb-btn wb-btn-success" onClick={handleCreate} disabled={!filename.trim()}>Create</button>
          <button className="wb-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAP EDITOR  The full tile editor
// ============================================================
const MapEditor = ({ mapData: initialData, tileList, mazeList, onSave, onDelete, onClone, onBack, showStatus }) => {
  const [mapState, setMapState] = useState(() => ({
    ...initialData,
    grid: initialData.grid.map(row => row.map(cell => ({
      t: 'grass1.jpg', p: false, l: null, s: null, nb: false, c: null, bb: false, cf: false,
      ...(cell || {})
    })))
  }));
  const [selectedTile, setSelectedTile] = useState('grass1.jpg');
  const [editMode, setEditMode] = useState('tile'); // tile, pass, link, shop, nb, convo
  const [tileSearch, setTileSearch] = useState('');
  const [showMonsters, setShowMonsters] = useState(false);
  const [showConvo, setShowConvo] = useState(false);
  const [showMapProps, setShowMapProps] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [isPainting, setIsPainting] = useState(false);
  const [selection, setSelection] = useState(null); // { startRow, startCol, endRow, endCol }
  const [isSelecting, setIsSelecting] = useState(false);
  const [zoom, setZoom] = useState(1);
  const gridRef = useRef(null);
  const scrollRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);

  // Measure the scroll container height to auto-size cells
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute cell size so rows fill available height (col-labels ~18px, padding 24px, border 2px)
  const baseCellSize = containerHeight && mapState.rows
    ? Math.floor((containerHeight - 42) / mapState.rows)
    : 48;
  const cellSize = Math.max(24, Math.min(baseCellSize, 96)); // clamp between 2496px

  // Undo/redo
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const currentGridRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const effectiveSize = Math.round(cellSize * zoom);

  // Keep grid ref in sync for undo/redo
  useEffect(() => { currentGridRef.current = mapState.grid; }, [mapState.grid]);

  const pushUndo = useCallback(() => {
    if (!currentGridRef.current) return;
    undoStackRef.current.push(JSON.parse(JSON.stringify(currentGridRef.current)));
    if (undoStackRef.current.length > 50) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    redoStackRef.current.push(JSON.parse(JSON.stringify(currentGridRef.current)));
    const prevGrid = undoStackRef.current.pop();
    setMapState(prev => ({ ...prev, grid: prevGrid }));
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    setIsDirty(true);
  }, []);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    undoStackRef.current.push(JSON.parse(JSON.stringify(currentGridRef.current)));
    const nextGrid = redoStackRef.current.pop();
    setMapState(prev => ({ ...prev, grid: nextGrid }));
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
    setIsDirty(true);
  }, []);

  const filteredTiles = useMemo(() => {
    if (!tileSearch) return tileList;
    return tileList.filter(t => t.toLowerCase().includes(tileSearch.toLowerCase()));
  }, [tileList, tileSearch]);

  const updateCell = useCallback((row, col, updates) => {
    setMapState(prev => {
      const newGrid = prev.grid.map(r => r.map(c => ({ t: 'grass1.jpg', p: false, l: null, s: null, nb: false, c: null, bb: false, ...c })));
      if (!newGrid[row] || !newGrid[row][col]) return prev;
      Object.assign(newGrid[row][col], updates);
      return { ...prev, grid: newGrid };
    });
    setIsDirty(true);
  }, []);

  // Flood fill (BFS)
  const floodFill = useCallback((startRow, startCol, newTile) => {
    setMapState(prev => {
      const grid = prev.grid;
      const targetTile = grid[startRow]?.[startCol]?.t;
      if (!targetTile || targetTile === newTile) return prev;

      const newGrid = grid.map(r => r.map(c => ({ ...c })));
      const visited = new Set();
      const queue = [[startRow, startCol]];
      visited.add(`${startRow},${startCol}`);

      while (queue.length > 0) {
        const [r, c] = queue.shift();
        newGrid[r][c] = { ...newGrid[r][c], t: newTile };

        for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]]) {
          const nr = r + dr, nc = c + dc;
          const key = `${nr},${nc}`;
          if (nr >= 0 && nr < prev.rows && nc >= 0 && nc < prev.cols &&
              !visited.has(key) && newGrid[nr]?.[nc]?.t === targetTile) {
            visited.add(key);
            queue.push([nr, nc]);
          }
        }
      }
      return { ...prev, grid: newGrid };
    });
    setIsDirty(true);
  }, []);

  // Normalized selection bounds
  const selBounds = useMemo(() => {
    if (!selection) return null;
    return {
      r1: Math.min(selection.startRow, selection.endRow),
      r2: Math.max(selection.startRow, selection.endRow),
      c1: Math.min(selection.startCol, selection.endCol),
      c2: Math.max(selection.startCol, selection.endCol),
    };
  }, [selection]);

  const applyToSelection = useCallback((updateFn) => {
    if (!selBounds) return;
    pushUndo();
    setMapState(prev => {
      const newGrid = prev.grid.map(r => r.map(c => ({ ...c })));
      for (let r = selBounds.r1; r <= selBounds.r2; r++) {
        for (let c = selBounds.c1; c <= selBounds.c2; c++) {
          if (newGrid[r]?.[c]) Object.assign(newGrid[r][c], updateFn(newGrid[r][c]));
        }
      }
      return { ...prev, grid: newGrid };
    });
    setIsDirty(true);
  }, [selBounds, pushUndo]);

  const handleCellClick = useCallback((row, col) => {
    const cell = mapState.grid[row] && mapState.grid[row][col];
    if (!cell) return;
    // Eyedropper: pick tile and switch back to tile mode
    if (editMode === 'pick') {
      setSelectedTile(cell.t || 'grass1.jpg');
      setEditMode('tile');
      return;
    }
    // Flood fill
    if (editMode === 'fill') {
      pushUndo();
      floodFill(row, col, selectedTile);
      return;
    }
    // Select mode handled by mouseDown/mouseUp
    if (editMode === 'select') return;
    pushUndo();
    switch (editMode) {
      case 'tile':
        updateCell(row, col, { t: selectedTile });
        break;
      case 'pass':
        updateCell(row, col, { p: !cell.p });
        break;
      case 'link': {
        const current = cell.l;
        const value = prompt('Enter link (format: map.dat X Y) or leave empty to clear:', current || '');
        if (value !== null) {
          updateCell(row, col, { l: value || null, p: value ? true : cell.p });
        }
        break;
      }
      case 'shop': {
        const current = cell.s;
        const value = prompt('Enter shop filename (e.g., myshop.dat) or empty to clear:', current || '');
        if (value !== null) {
          updateCell(row, col, { s: value || null, p: value ? true : cell.p });
        }
        break;
      }
      case 'nb':
        updateCell(row, col, { nb: !cell.nb });
        break;
      case 'bb':
        updateCell(row, col, { bb: !cell.bb });
        break;
      case 'cf':
        updateCell(row, col, { cf: !cell.cf });
        break;
      case 'convo': {
        const current = cell.c;
        const value = prompt('Enter conversation node name or empty to clear:', current || '');
        if (value !== null) {
          updateCell(row, col, { c: value || null });
        }
        break;
      }
      default: break;
    }
  }, [editMode, selectedTile, mapState.grid, updateCell, pushUndo, floodFill]);

  const handleCellMouseDown = useCallback((row, col) => {
    if (editMode === 'select') {
      setIsSelecting(true);
      setSelection({ startRow: row, startCol: col, endRow: row, endCol: col });
      return;
    }
    if (editMode === 'tile') {
      pushUndo();
      setIsPainting(true);
      updateCell(row, col, { t: selectedTile });
    }
  }, [editMode, selectedTile, updateCell, pushUndo]);

  const handleCellMouseEnter = useCallback((row, col) => {
    setHoveredCell({ row, col });
    if (isSelecting && editMode === 'select') {
      setSelection(prev => prev ? { ...prev, endRow: row, endCol: col } : null);
      return;
    }
    if (isPainting && editMode === 'tile') {
      updateCell(row, col, { t: selectedTile });
    }
  }, [isPainting, isSelecting, editMode, selectedTile, updateCell]);

  const handleMouseUp = useCallback(() => {
    setIsPainting(false);
    setIsSelecting(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const handleSave = useCallback(() => {
    onSave(mapState);
    setIsDirty(false);
  }, [onSave, mapState]);

  // Keyboard shortcuts
  useEffect(() => {
    const modeKeys = { '1': 'tile', '2': 'fill', '3': 'pick', '4': 'select', '5': 'pass', '6': 'link', '7': 'shop', '8': 'nb', '9': 'convo', '0': 'bb' };
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        setSelection(null);
      } else if (!e.ctrlKey && !e.metaKey && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT') {
        if (modeKeys[e.key]) setEditMode(modeKeys[e.key]);
        else if (e.key === 'i') setEditMode('pick');
        else if (e.key === 'f') setEditMode('fill');
        else if (e.key === 'c') setEditMode('cf');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, handleSave]);

  // Unsaved changes warning
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Parse NPC sprite positions from convo.process node
  const npcSprites = useMemo(() => {
    const sprites = {}; // key: "row,col" -> { sprite, node }
    const process = mapState.convo?.process;
    if (!process) return sprites;
    for (const line of Object.values(process)) {
      // Match: npc ROW COL sprite.gif nodeName (with optional "if...then" prefix)
      const npcMatch = String(line).match(/npc\s+(\d+)\s+(\d+)\s+(\S+\.gif)\s+(\S+)/);
      if (npcMatch) {
        sprites[`${npcMatch[1]},${npcMatch[2]}`] = { sprite: npcMatch[3], node: npcMatch[4] };
      }
    }
    return sprites;
  }, [mapState.convo]);

  // Overlay color for modes
  const getCellOverlay = (cell) => {
    switch (editMode) {
      case 'pass':
        return cell.p ? 'rgba(66,133,244,0.4)' : 'rgba(234,67,53,0.4)';
      case 'link':
        return cell.l ? 'rgba(251,188,4,0.5)' : 'transparent';
      case 'shop':
        return cell.s ? 'rgba(251,188,4,0.5)' : 'transparent';
      case 'nb':
        return cell.nb ? 'rgba(52,168,83,0.5)' : 'transparent';
      case 'bb':
        return cell.bb ? 'rgba(218,165,32,0.6)' : 'transparent';
      case 'cf':
        return cell.cf ? 'rgba(255,100,0,0.6)' : 'transparent';
      case 'convo':
        return cell.c ? 'rgba(168,52,235,0.5)' : 'transparent';
      default:
        return 'transparent';
    }
  };

  // Get abbreviated link target for display
  const getLinkArrow = (cell, row, col) => {
    if (!cell.l) return null;
    const parts = cell.l.split(' ');
    const target = parts[0]?.replace('.dat', '') || '?';
    // Determine direction arrow based on edge position
    let arrow = '';
    if (row === 0) arrow = '';
    else if (row === mapState.rows - 1) arrow = '';
    else if (col === 0) arrow = '';
    else if (col === mapState.cols - 1) arrow = '';
    else arrow = ''; // interior link (e.g. door/dungeon entrance)
    return { arrow, target };
  };

  const getCellLabel = (cell) => {
    switch (editMode) {
      case 'pass': return cell.p ? '' : '';
      case 'link': return cell.l ? '' : '';
      case 'shop': return cell.s ? '' : '';
      case 'nb': return cell.nb ? 'NB' : '';
      case 'bb': return cell.bb ? 'BB' : '';
      case 'cf': return cell.cf ? '🔥' : '';
      case 'convo': return cell.c ? '' : '';
      default: return '';
    }
  };

  return (
    <div className="wb-editor" onMouseUp={handleMouseUp}>
      {/* Toolbar */}
      <div className="wb-toolbar">
        <button className="wb-btn" onClick={() => {
          if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
          onBack();
        }}>World</button>
        <div className="wb-toolbar-divider" />

        {/* Undo/Redo */}
        <button className="wb-btn" onClick={undo} disabled={!canUndo} title="Undo (Z)"></button>
        <button className="wb-btn" onClick={redo} disabled={!canRedo} title="Redo (Z)"></button>
        <div className="wb-toolbar-divider" />

        {/* Edit modes */}
        {[
          { id: 'tile', label: '✨ Tile', title: '1  Paint tiles' },
          { id: 'fill', label: '✨ Fill', title: '2  Flood fill area' },
          { id: 'pick', label: '✨ Pick', title: '3  Eyedropper (I)' },
          { id: 'select', label: '✨ Select', title: '4  Select rectangle, Esc clear' },
          { id: 'pass', label: '✨ Pass', title: '5  Toggle passability' },
          { id: 'link', label: '✨ Link', title: '6  Set map links' },
          { id: 'shop', label: '✨ Shop', title: '7  Set shop references' },
          { id: 'nb', label: '✨ NB', title: '8  Toggle no-battle' },
          { id: 'convo', label: '✨ Convo', title: '9  Set conversation nodes' },
          { id: 'bb', label: '📜 BB', title: '0  Toggle bounty board' },
          { id: 'cf', label: '🔥 Camp', title: 'C  Toggle campfire' },
        ].map(mode => (
          <button
            key={mode.id}
            className={`wb-btn wb-mode-btn ${editMode === mode.id ? 'active' : ''}`}
            onClick={() => setEditMode(mode.id)}
            title={mode.title}
          >
            {mode.label}
          </button>
        ))}

        <div className="wb-toolbar-divider" />
        <button className={`wb-btn ${showMonsters ? 'active' : ''}`} onClick={() => setShowMonsters(!showMonsters)}>Monsters</button>
        <button className={`wb-btn ${showConvo ? 'active' : ''}`} onClick={() => setShowConvo(!showConvo)}>Convos</button>
        <button className={`wb-btn ${showMapProps ? 'active' : ''}`} onClick={() => setShowMapProps(!showMapProps)}>Props</button>

        <div className="wb-toolbar-spacer" />

        {/* Zoom controls */}
        <button className="wb-btn wb-btn-xs" onClick={() => setZoom(z => Math.max(0.3, z - 0.15))}>✖</button>
        <span className="wb-zoom-label">{Math.round(zoom * 100)}%</span>
        <button className="wb-btn wb-btn-xs" onClick={() => setZoom(z => Math.min(3, z + 0.15))}>+</button>

        <div className="wb-toolbar-divider" />
        <button className={`wb-btn wb-btn-success ${isDirty ? 'wb-pulse' : ''}`} onClick={handleSave}>
          💾 Save{isDirty ? ' *' : ''}
        </button>
        <button className="wb-btn" onClick={() => onClone(mapState.filename)} title="Clone this map">
           Clone
        </button>
        <button className="wb-btn wb-btn-danger" onClick={() => onDelete(mapState.filename)} title="Delete this map">
          </button>
      </div>

      <div className="wb-editor-body">
        {/* Tile Palette (left) */}
        <div className="wb-tile-palette">
          <div className="wb-palette-header">
            <input
              type="text"
              placeholder="Search tiles..."
              value={tileSearch}
              onChange={e => setTileSearch(e.target.value)}
              className="wb-input wb-input-sm"
            />
            <span className="wb-palette-count">{filteredTiles.length}</span>
          </div>
          <div className="wb-palette-tiles">
            {filteredTiles.map(tile => (
              <div
                key={tile}
                className={`wb-palette-tile ${selectedTile === tile ? 'selected' : ''}`}
                onClick={() => setSelectedTile(tile)}
                title={tile}
              >
                <img
                  src={`${TILE_BASE}${tile}`}
                  alt={tile}
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>
          {selectedTile && (
            <div className="wb-palette-selected">
              <img src={`${TILE_BASE}${selectedTile}`} alt={selectedTile} />
              <span>{selectedTile}</span>
            </div>
          )}
        </div>

        {/* Map Grid (center) */}
        <div className="wb-grid-container" ref={gridRef}>
          {/* Hovered cell info */}
          {hoveredCell && mapState.grid[hoveredCell.row] && mapState.grid[hoveredCell.row][hoveredCell.col] && (() => {
            const cell = mapState.grid[hoveredCell.row][hoveredCell.col];
            return (
              <div className="wb-cell-info">
                [{hoveredCell.row},{hoveredCell.col}] {cell.t || '?'}
                {cell.p ? ' pass' : ' pass'}
                {cell.l && ` ${cell.l}`}
                {cell.s && ` ${cell.s}`}
                {cell.nb && ' NB'}
                {cell.cf && ' 🔥CF'}
                {cell.c && ` ${cell.c}`}
              </div>
            );
          })()}
          {/* Selection action bar */}
          {selBounds && editMode === 'select' && (
            <div className="wb-selection-bar">
              <span>Selected: [{selBounds.r1},{selBounds.c1}]  [{selBounds.r2},{selBounds.c2}] ({(selBounds.r2-selBounds.r1+1)*(selBounds.c2-selBounds.c1+1)} cells)</span>
              <button className="wb-btn wb-btn-xs" onClick={() => applyToSelection(() => ({ t: selectedTile }))}>Fill Tile</button>
              <button className="wb-btn wb-btn-xs" onClick={() => applyToSelection(() => ({ p: true }))}>All Pass</button>
              <button className="wb-btn wb-btn-xs" onClick={() => applyToSelection(() => ({ p: false }))}>All Block</button>
              <button className="wb-btn wb-btn-xs" onClick={() => applyToSelection(() => ({ nb: true }))}>All NB</button>
              <button className="wb-btn wb-btn-xs" onClick={() => applyToSelection(() => ({ nb: false }))}>Clear NB</button>
              <button className="wb-btn wb-btn-xs" onClick={() => applyToSelection(() => ({ l: null, s: null, c: null }))}>Clear Props</button>
              <button className="wb-btn wb-btn-xs" onClick={() => setSelection(null)}>Deselect</button>
            </div>
          )}
          <div className="wb-grid-scroll" ref={scrollRef}>
            <div className="wb-grid-with-labels">
              {/* Column labels */}
              <div className="wb-col-labels" style={{ marginLeft: 28, display: 'flex' }}>
                {Array.from({ length: mapState.cols }, (_, c) => (
                  <span key={c} className="wb-axis-label" style={{ width: effectiveSize }}>{c}</span>
                ))}
              </div>
              <div style={{ display: 'flex' }}>
                {/* Row labels */}
                <div className="wb-row-labels" style={{ display: 'flex', flexDirection: 'column' }}>
                  {Array.from({ length: mapState.rows }, (_, r) => (
                    <span key={r} className="wb-axis-label" style={{ height: effectiveSize, lineHeight: `${effectiveSize}px`, width: 28 }}>{r}</span>
                  ))}
                </div>
                {/* Grid */}
                <div
                  className="wb-grid"
                  style={{
                    gridTemplateColumns: `repeat(${mapState.cols}, ${effectiveSize}px)`,
                    gridTemplateRows: `repeat(${mapState.rows}, ${effectiveSize}px)`,
                  }}
                >
                  {mapState.grid.map((row, r) =>
                    row.map((cell, c) => {
                      const safeCell = cell || { t: 'grass1.jpg', p: false, l: null, s: null, nb: false, c: null };
                      const inSelection = selBounds && r >= selBounds.r1 && r <= selBounds.r2 && c >= selBounds.c1 && c <= selBounds.c2;
                      return (
                      <div
                        key={`${r}-${c}`}
                        className={`wb-grid-cell ${inSelection ? 'wb-selected' : ''}`}
                        style={{
                          width: effectiveSize,
                          height: effectiveSize,
                          backgroundImage: `url(${TILE_BASE}${safeCell.t || 'grass1.jpg'})`,
                          backgroundSize: 'cover',
                        }}
                        onMouseDown={(e) => { e.preventDefault(); handleCellMouseDown(r, c); }}
                        onMouseEnter={() => handleCellMouseEnter(r, c)}
                        onClick={() => { if (editMode !== 'tile' && editMode !== 'select') handleCellClick(r, c); }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          const info = `[${r},${c}] ${safeCell.t}\nPass: ${safeCell.p}\nLink: ${safeCell.l || 'none'}\nShop: ${safeCell.s || 'none'}\nNB: ${safeCell.nb}\nConvo: ${safeCell.c || 'none'}`;
                          alert(info);
                        }}
                      >
                        <div
                          className="wb-grid-cell-overlay"
                          style={{ backgroundColor: getCellOverlay(safeCell) }}
                        >
                          <span className="wb-grid-cell-label">{getCellLabel(safeCell)}</span>
                        </div>
                        {/* Link visualization */}
                        {safeCell.l && editMode === 'link' && (() => {
                          const info = getLinkArrow(safeCell, r, c);
                          return info ? (
                            <div className="wb-link-badge">
                              <span className="wb-link-arrow">{info.arrow}</span>
                              <span className="wb-link-target">{info.target}</span>
                            </div>
                          ) : null;
                        })()}
                        {/* NPC sprite marker */}
                        {npcSprites[`${r},${c}`] && (
                          <div className="wb-npc-marker" title={`NPC: ${npcSprites[`${r},${c}`].sprite}  ${npcSprites[`${r},${c}`].node}`}>
                            <span className="wb-npc-icon"></span>
                          </div>
                        )}
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right panels */}
        <div className="wb-right-panels">
          {showMonsters && (
            <MonsterPanel
              monsters={mapState.monsters}
              onChange={(monsters) => { setMapState(prev => ({ ...prev, monsters })); setIsDirty(true); }}
            />
          )}
          {showConvo && (
            <ConvoPanel
              convo={mapState.convo}
              onChange={(convo) => { setMapState(prev => ({ ...prev, convo })); setIsDirty(true); }}
              mapGrid={mapState.grid}
              onRenameConvoNode={(oldName, newName) => {
                setMapState(prev => {
                  // Update all cell .c references on the grid
                  const newGrid = prev.grid.map(row => row.map(cell => {
                    if (cell && cell.c === oldName) return { ...cell, c: newName };
                    return cell;
                  }));
                  // Rename inside convo: node key + all goto/option/npc/fight/check/if references
                  const newConvo = {};
                  for (const [nodeName, lines] of Object.entries(prev.convo || {})) {
                    const key = nodeName === oldName ? newName : nodeName;
                    const newLines = {};
                    for (const [idx, line] of Object.entries(lines || {})) {
                      newLines[idx] = String(line)
                        .replace(new RegExp(`(goto|gosub)\\s+${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), `$1 ${newName}`)
                        .replace(new RegExp(`(option)\\s+${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), `$1 ${newName}`)
                        .replace(new RegExp(`(then\\s+goto|then\\s+gosub)\\s+${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), `$1 ${newName}`)
                        .replace(new RegExp(`(npc\\s+\\d+\\s+\\d+\\s+\\S+\\s+)${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'), `$1${newName}`)
                        .replace(new RegExp(`(check\\s+\\S+\\s+\\d+\\s+)${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`, 'g'), `$1${newName}$2`)
                        .replace(new RegExp(`(check\\s+\\S+\\s+\\d+\\s+\\S+\\s+)${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`, 'g'), `$1${newName}$2`)
                        .replace(new RegExp(`(fight\\s+)${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s)`, 'g'), `$1${newName}$2`)
                        .replace(new RegExp(`(fight\\s+\\S+\\s+)${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s)`, 'g'), `$1${newName}$2`)
                        .replace(new RegExp(`(fight\\s+\\S+\\s+\\S+\\s+)${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s)`, 'g'), `$1${newName}$2`);
                    }
                    newConvo[key] = newLines;
                  }
                  return { ...prev, grid: newGrid, convo: newConvo };
                });
                setIsDirty(true);
              }}
            />
          )}
          {showMapProps && (
            <MapPropsPanel
              mapState={mapState}
              onChange={(updates) => { setMapState(prev => ({ ...prev, ...updates })); setIsDirty(true); }}
              onResize={(newRows, newCols) => {
                pushUndo();
                setMapState(prev => {
                  const grid = [];
                  for (let r = 0; r < newRows; r++) {
                    const row = [];
                    for (let c = 0; c < newCols; c++) {
                      row.push(prev.grid[r]?.[c] || { t: 'grass1.jpg', p: false, l: null, s: null, nb: false, c: null });
                    }
                    grid.push(row);
                  }
                  return { ...prev, rows: newRows, cols: newCols, grid };
                });
                setIsDirty(true);
                showStatus(`Resized to ${newRows}${newCols}`);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MONSTER PANEL
// ============================================================
const MonsterPanel = ({ monsters, onChange }) => {
  const [newRange, setNewRange] = useState('');
  const [newMonster, setNewMonster] = useState('');

  const addMonster = () => {
    if (!newRange || !newMonster) return;
    onChange({ ...monsters, [newRange]: newMonster });
    setNewRange('');
    setNewMonster('');
  };

  const removeMonster = (range) => {
    const updated = { ...monsters };
    delete updated[range];
    onChange(updated);
  };

  return (
    <div className="wb-panel">
      <h3>Monster Spawns</h3>
      <p className="wb-panel-hint">Range format: start-end (weighted random). e.g., 1-3:Rat means rolls 1-3 = Rat</p>
      <div className="wb-monster-list">
        {Object.entries(monsters).map(([range, name]) => (
          <div key={range} className="wb-monster-entry">
            <span className="wb-monster-range">{range}</span>
            <span className="wb-monster-name">{name}</span>
            <button className="wb-btn wb-btn-xs wb-btn-danger" onClick={() => removeMonster(range)}>✖</button>
          </div>
        ))}
      </div>
      <div className="wb-panel-add-row">
        <input type="text" value={newRange} onChange={e => setNewRange(e.target.value)} placeholder="1-3" className="wb-input wb-input-xs" />
        <input type="text" value={newMonster} onChange={e => setNewMonster(e.target.value)} placeholder="Monster name" className="wb-input wb-input-sm" />
        <button className="wb-btn wb-btn-xs wb-btn-success" onClick={addMonster}>+</button>
      </div>
    </div>
  );
};

// ============================================================
// CONVOCODE COMMAND REFERENCE DATA
// ============================================================
const CONVO_COMMANDS = [
  { cmd: 'say', syntax: "say 'Text here'", desc: 'Display text to the player. Concatenate vars with dots.', cat: 'output', template: "say 'Hello!'" },
  { cmd: 'option', syntax: "option nodeName 'Button text'", desc: 'Show a clickable choice that jumps to nodeName.', cat: 'output', template: "option nodeName 'Choose this'" },
  { cmd: 'goto', syntax: 'goto nodeName', desc: 'Jump execution to another conversation node.', cat: 'flow', template: 'goto nodeName' },
  { cmd: 'gosub', syntax: 'gosub nodeName', desc: 'Call another node like a subroutine, then resume here.', cat: 'flow', template: 'gosub nodeName' },
  { cmd: 'return', syntax: 'return', desc: 'Return from a gosub call.', cat: 'flow', template: 'return' },
  { cmd: 'end', syntax: 'end', desc: 'Stop execution immediately.', cat: 'flow', template: 'end' },
  { cmd: 'if', syntax: "if CONDITION then COMMAND", desc: 'Inline conditional. Runs COMMAND only when CONDITION is true.', cat: 'logic', template: "if $v['level']>=10 then goto qualified" },
  { cmd: 'blockif', syntax: 'blockif CONDITION\\n  ...\\nendif', desc: 'Block conditional. Pair with elseif/else/endif.', cat: 'logic', template: "blockif $v['level']>=10\n  say 'You are strong enough.'\nelse\n  say 'Come back when stronger.'\nendif" },
  { cmd: 'elseif', syntax: 'elseif CONDITION', desc: 'Alternate branch inside a blockif.', cat: 'logic', template: 'elseif CONDITION' },
  { cmd: 'else', syntax: 'else', desc: 'Default branch inside a blockif.', cat: 'logic', template: 'else' },
  { cmd: 'endif', syntax: 'endif', desc: 'Closes a blockif/elseif/else block.', cat: 'logic', template: 'endif' },
  { cmd: 'do', syntax: "do $v['xp']+=500", desc: 'Execute a variable assignment/expression.', cat: 'vars', template: "do $v['xp']+=1000" },
  { cmd: 'setVar', syntax: 'setVar questGroup varName value', desc: 'Set a quest variable. e.g. setVar thormir quest01 2', cat: 'vars', template: 'setVar questGroup varName 1' },
  { cmd: 'givegp', syntax: 'givegp 500', desc: 'Give gold to the player.', cat: 'economy', template: 'givegp 500' },
  { cmd: 'takegp', syntax: 'takegp 500 error goto cantAfford', desc: 'Take gold. Optional error handler if not enough.', cat: 'economy', template: 'takegp 100' },
  { cmd: 'give', syntax: 'give type:weapon,name:Sword,...', desc: 'Give an item to the player inventory.', cat: 'items', template: "give type:weapon,name:Iron Sword,damage:2d6,cost:500" },
  { cmd: 'take', syntax: 'take QTY name:ItemName', desc: 'Take items. Sets $qty to amount actually taken.', cat: 'items', template: "take 1 name:Quest Item" },
  { cmd: 'fight', syntax: 'fight wonNode lostNode ranNode name:...,hp:...', desc: 'Trigger a scripted combat encounter.', cat: 'combat', template: "fight won lost ran name:Goblin,type:humanoid(lesser),hp:100,att:20,def:10,STR:10,AGL:10,VIT:10,MEN:8,CHA:6,xp:200,level:5,pic:goblin.jpg" },
  { cmd: 'check', syntax: 'check STAT DC passNode failNode', desc: 'Roll a d20+modifier vs DC. Branch on result.', cat: 'combat', template: "check STR 15 passNode failNode" },
  { cmd: 'npc', syntax: 'npc ROW COL sprite.gif nodeName', desc: 'Place an NPC sprite on the map grid.', cat: 'map', template: "npc 5 5 villager1.gif talkNode" },
  { cmd: 'warp', syntax: "give type:warp,loc:maze.dat,x:5,y:3,inst:t", desc: 'Teleport the player to another map.', cat: 'map', template: "give type:warp,loc:maze.dat,x:5,y:3,inst:t" },
  { cmd: 'shop', syntax: 'shop', desc: 'Open the shop interface (for shop maps).', cat: 'map', template: 'shop' },
  { cmd: 'inn', syntax: 'inn COST', desc: 'Rest at an inn for gold, restoring HP/MP.', cat: 'economy', template: 'inn 50' },
  { cmd: 'heal', syntax: 'heal', desc: 'Fully heal the player.', cat: 'combat', template: 'heal' },
  { cmd: 'cast', syntax: 'cast SPELL', desc: 'Cast a spell effect on the player.', cat: 'combat', template: 'cast cure' },
  { cmd: 'wait', syntax: 'wait MS', desc: 'Pause execution for milliseconds.', cat: 'flow', template: 'wait 1000' },
  { cmd: 'skip', syntax: 'skip N', desc: 'Skip the next N lines of code.', cat: 'flow', template: 'skip 1' },
  { cmd: 'giveitem', syntax: "giveitem 'Item Name'", desc: 'Give a simple quest item to the player.', cat: 'items', template: "giveitem 'Quest Item'" },
  { cmd: 'takeitem', syntax: "takeitem 'Item Name'", desc: 'Remove a simple quest item from the player.', cat: 'items', template: "takeitem 'Quest Item'" },
  { cmd: 'takekey', syntax: 'takekey QTY keyName', desc: 'Take/count key items. QTY: number, "all", or "count". Sets $qty.', cat: 'items', template: 'takekey count Skeleton Key' },
];

const CONVO_CMD_CATEGORIES = [
  { id: 'output', label: '✨ Output', color: '#4ec9b0' },
  { id: 'flow', label: '✨ Flow', color: '#569cd6' },
  { id: 'logic', label: '✨ Logic', color: '#c586c0' },
  { id: 'vars', label: '✨ Variables', color: '#9cdcfe' },
  { id: 'economy', label: '✨ Economy', color: '#dcdcaa' },
  { id: 'items', label: '✨ Items', color: '#ce9178' },
  { id: 'combat', label: '✨ Combat', color: '#f44747' },
  { id: 'map', label: '✨ Map', color: '#d7ba7d' },
];

// Syntax highlight color mapping
const CMD_COLORS = {
  say: '#4ec9b0', option: '#4ec9b0',
  goto: '#569cd6', gosub: '#569cd6', return: '#569cd6', end: '#569cd6', wait: '#569cd6',
  if: '#c586c0', blockif: '#c586c0', elseif: '#c586c0', else: '#c586c0', endif: '#c586c0', then: '#c586c0',
  do: '#9cdcfe', setvar: '#9cdcfe',
  givegp: '#dcdcaa', takegp: '#dcdcaa', inn: '#dcdcaa', shop: '#dcdcaa',
  give: '#ce9178', take: '#ce9178', takekey: '#ce9178',
  fight: '#f44747', check: '#f44747', attack: '#f44747', heal: '#f44747', cast: '#f44747',
  npc: '#d7ba7d', warp: '#d7ba7d', skip: '#569cd6', input: '#4ec9b0',
};

// ============================================================
// CONVERSATION PANEL (Enhanced)
// ============================================================
const ConvoPanel = ({ convo, onChange, mapGrid, onRenameConvoNode }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [editText, setEditText] = useState('');
  const [newNodeName, setNewNodeName] = useState('');
  const [showRef, setShowRef] = useState(false);
  const [refFilter, setRefFilter] = useState('all');
  const [nodeSearch, setNodeSearch] = useState('');
  const [renamingNode, setRenamingNode] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const renameInputRef = useRef(null);

  const nodeNames = Object.keys(convo || {});

  // Filter nodes by search
  const filteredNodeNames = useMemo(() => {
    if (!nodeSearch.trim()) return nodeNames;
    const q = nodeSearch.toLowerCase();
    return nodeNames.filter(name => {
      if (name.toLowerCase().includes(q)) return true;
      const lines = convo[name] || {};
      return Object.values(lines).some(l => String(l).toLowerCase().includes(q));
    });
  }, [nodeNames, nodeSearch, convo]);

  const selectNode = (name) => {
    setSelectedNode(name);
    const lines = convo[name] || {};
    const sorted = Object.entries(lines)
      .filter(([k]) => k !== '_raw')
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
    setEditText(sorted.map(([, val]) => val).join('\n'));
  };

  const saveNode = () => {
    if (!selectedNode) return;
    const lines = {};
    editText.split('\n').forEach((line, i) => {
      if (line.trim()) lines[i] = line;
    });
    onChange({ ...convo, [selectedNode]: lines });
  };

  const addNode = () => {
    if (!newNodeName.trim()) return;
    onChange({ ...convo, [newNodeName.trim()]: { 0: "say 'Hello!'" } });
    setNewNodeName('');
    selectNode(newNodeName.trim());
  };

  const deleteNode = (name) => {
    if (!window.confirm(`Delete conversation node "${name}"?`)) return;
    const updated = { ...convo };
    delete updated[name];
    onChange(updated);
    if (selectedNode === name) { setSelectedNode(null); setEditText(''); }
  };

  const duplicateNode = (name) => {
    let newName = name + '_copy';
    let n = 2;
    while (convo[newName]) { newName = `${name}_copy${n}`; n++; }
    onChange({ ...convo, [newName]: { ...convo[name] } });
    selectNode(newName);
  };

  // Rename node 
  const startRename = (name, e) => {
    e.stopPropagation();
    setRenamingNode(name);
    setRenameValue(name);
    setTimeout(() => renameInputRef.current?.select(), 0);
  };

  const commitRename = () => {
    if (!renamingNode || !renameValue.trim() || renameValue.trim() === renamingNode) {
      setRenamingNode(null);
      return;
    }
    const newName = renameValue.trim();
    if (convo[newName]) {
      alert(`Node "${newName}" already exists.`);
      return;
    }
    onRenameConvoNode(renamingNode, newName);
    if (selectedNode === renamingNode) setSelectedNode(newName);
    setRenamingNode(null);
  };

  // Quick-create missing node 
  const quickCreateNode = (name) => {
    onChange({ ...convo, [name]: { 0: "say 'Hello!'" } });
    selectNode(name);
  };

  const insertSnippet = (template) => {
    const ta = textareaRef.current;
    if (!ta) { setEditText(prev => prev ? prev + '\n' + template : template); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = editText.substring(0, start);
    const after = editText.substring(end);
    // Insert on a new line if cursor isn't at line start
    const needNewline = before.length > 0 && before[before.length - 1] !== '\n';
    const insert = (needNewline ? '\n' : '') + template;
    setEditText(before + insert + after);
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + insert.length; }, 0);
  };

  // Sync scroll between textarea, highlight overlay, and line numbers
  const lineNumRef = useRef(null);
  const syncScroll = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = ta.scrollTop;
      highlightRef.current.scrollLeft = ta.scrollLeft;
    }
    if (lineNumRef.current) {
      lineNumRef.current.scrollTop = ta.scrollTop;
    }
  };

  // NPC line detection 
  const npcLines = useMemo(() => {
    const result = [];
    const lines = editText.split('\n');
    lines.forEach((line, i) => {
      const m = line.trim().match(/^npc\s+(\d+)\s+(\d+)\s+(\S+\.gif)\s+(\S+)/i);
      if (m) result.push({ lineIdx: i, row: parseInt(m[1]), col: parseInt(m[2]), sprite: m[3], node: m[4] });
    });
    return result;
  }, [editText]);

  // Syntax highlighting 
  const highlightLine = (line) => {
    const trimmed = line.trimStart();
    const indent = line.length - trimmed.length;
    const indentStr = line.substring(0, indent);

    // Extract command keyword
    const cmdMatch = trimmed.match(/^(\w+)/);
    if (!cmdMatch) return [{ text: line, color: '#6a6a7a' }]; // comment / blank

    const cmd = cmdMatch[1].toLowerCase();
    const color = CMD_COLORS[cmd] || '#d4d0c8';
    const rest = trimmed.substring(cmdMatch[1].length);

    const spans = [];
    if (indent > 0) spans.push({ text: indentStr, color: 'transparent' });
    spans.push({ text: cmdMatch[1], color, bold: true });

    // Highlight strings in rest
    let remaining = rest;
    const stringRegex = /'([^'\\]*(?:\\.[^'\\]*)*)'/g;
    let lastIdx = 0;
    let m;
    const restSpans = [];
    while ((m = stringRegex.exec(remaining)) !== null) {
      if (m.index > lastIdx) restSpans.push({ text: remaining.substring(lastIdx, m.index), color: '#d4d0c8' });
      restSpans.push({ text: m[0], color: '#ce9178' }); // string = orange
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < remaining.length) restSpans.push({ text: remaining.substring(lastIdx), color: '#d4d0c8' });

    // Inside rest, also highlight "then" keyword
    const finalSpans = [];
    for (const s of restSpans) {
      if (s.color !== '#ce9178') {
        const thenParts = s.text.split(/([ ]then[ ])/i);
        for (const p of thenParts) {
          if (p.toLowerCase() === ' then ') finalSpans.push({ text: p, color: '#c586c0', bold: true });
          else finalSpans.push({ text: p, color: s.color });
        }
      } else {
        finalSpans.push(s);
      }
    }

    return [...spans, ...finalSpans];
  };

  // Validation 
  const validation = useMemo(() => {
    const warnings = [];
    if (!convo) return warnings;
    const allNodes = new Set(Object.keys(convo));

    // Gather all goto/option targets, and which nodes reference which
    const referencedNodes = new Set();
    const nodeEdges = {}; // nodeName -> { inbound: Set, outbound: Set }
    for (const name of allNodes) nodeEdges[name] = { inbound: new Set(), outbound: new Set() };

    // Find cell references to convo nodes
    const cellRefs = new Set();
    if (mapGrid) {
      for (const row of mapGrid) {
        for (const cell of row) {
          if (cell && cell.c) cellRefs.add(cell.c);
        }
      }
    }

    for (const [nodeName, lines] of Object.entries(convo)) {
      if (!lines || typeof lines !== 'object') continue;
      const sorted = Object.entries(lines).filter(([k]) => k !== '_raw').sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
      const lineTexts = sorted.map(([, v]) => v);

      if (lineTexts.length === 0) {
        warnings.push({ type: 'empty', node: nodeName, msg: `Node "${nodeName}" is empty` });
      }

      let blockIfDepth = 0;
      for (const line of lineTexts) {
        const t = (line || '').trim();
        const cmdM = t.match(/^(\w+)/);
        if (!cmdM) continue;
        const cmd = cmdM[1].toLowerCase();

        // Track blockif nesting
        if (cmd === 'blockif') blockIfDepth++;
        if (cmd === 'endif') blockIfDepth--;

        // Extract goto targets
        if (cmd === 'goto' || cmd === 'gosub') {
          const target = t.split(/\s+/)[1];
          if (target) {
            referencedNodes.add(target);
            if (nodeEdges[target]) nodeEdges[target].inbound.add(nodeName);
            if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(target);
            if (!allNodes.has(target)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"${cmd} ${target}"  node "${target}" does not exist`, target });
            }
          }
        }

        // Extract option targets
        if (cmd === 'option') {
          const target = t.split(/\s+/)[1];
          if (target) {
            referencedNodes.add(target);
            if (nodeEdges[target]) nodeEdges[target].inbound.add(nodeName);
            if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(target);
            if (!allNodes.has(target)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"option ${target}"  node "${target}" does not exist`, target });
            }
          }
        }

        // if...then goto/gosub
        if (cmd === 'if') {
          const thenMatch = t.match(/then\s+(goto|gosub)\s+(\S+)/i);
          if (thenMatch) {
            const tgt = thenMatch[2];
            referencedNodes.add(tgt);
            if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
            if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
            if (!allNodes.has(tgt)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"if...then ${thenMatch[1]} ${tgt}"  node "${tgt}" does not exist`, target: tgt });
            }
          }

          // if...then option NODE 'label'
          const thenOption = t.match(/then\s+option\s+(\S+)/i);
          if (thenOption) {
            const tgt = thenOption[1];
            referencedNodes.add(tgt);
            if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
            if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
            if (!allNodes.has(tgt)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"if...then option ${tgt}"  node "${tgt}" does not exist`, target: tgt });
            }
          }

          // if...then check STAT DC passNode failNode
          const thenCheck = t.match(/then\s+check\s+\S+\s+\S+\s+(\S+)\s+(\S+)/i);
          if (thenCheck) {
            [thenCheck[1], thenCheck[2]].forEach(tgt => {
              if (tgt) {
                referencedNodes.add(tgt);
                if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
                if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
                if (!allNodes.has(tgt)) {
                  warnings.push({ type: 'dead-link', node: nodeName, msg: `"if...then check ... ${tgt}"  node "${tgt}" does not exist`, target: tgt });
                }
              }
            });
          }

          // if...then npc X Y pic NODE
          const thenNpc = t.match(/then\s+npc\s+\S+\s+\S+\s+\S+\s+(\S+)/i);
          if (thenNpc) {
            const tgt = thenNpc[1];
            referencedNodes.add(tgt);
            if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
            if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
            if (!allNodes.has(tgt)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"if...then npc ... ${tgt}"  node "${tgt}" does not exist`, target: tgt });
            }
          }

          // if...then fight wonNode lostNode ranNode ...
          const thenFight = t.match(/then\s+fight\s+(\S+)\s+(\S+)\s+(\S+)/i);
          if (thenFight) {
            [thenFight[1], thenFight[2], thenFight[3]].forEach(tgt => {
              if (tgt && !tgt.includes(':')) {
                referencedNodes.add(tgt);
                if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
                if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
                if (!allNodes.has(tgt)) {
                  warnings.push({ type: 'dead-link', node: nodeName, msg: `"if...then fight ... ${tgt}"  node "${tgt}" does not exist`, target: tgt });
                }
              }
            });
          }
        }

        // npc command references a node
        if (cmd === 'npc') {
          const parts = t.split(/\s+/);
          if (parts.length >= 5) {
            const tgt = parts[4];
            referencedNodes.add(tgt);
            if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
            if (!allNodes.has(tgt)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"npc ... ${tgt}"  node "${tgt}" does not exist`, target: tgt });
            }
          }
        }

        // fight wonNode lostNode ranNode ...
        if (cmd === 'fight') {
          const parts = t.split(/\s+/);
          [parts[1], parts[2], parts[3]].forEach(tgt => {
            if (tgt && !tgt.includes(':')) {
              referencedNodes.add(tgt);
              if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
              if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
              if (!allNodes.has(tgt)) {
                warnings.push({ type: 'dead-link', node: nodeName, msg: `"fight ... ${tgt}"  node "${tgt}" does not exist`, target: tgt });
              }
            }
          });
        }

        // check STAT DC passNode failNode
        if (cmd === 'check') {
          const parts = t.split(/\s+/);
          [parts[3], parts[4]].forEach(tgt => {
            if (tgt) {
              referencedNodes.add(tgt);
              if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
              if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
              if (!allNodes.has(tgt)) {
                warnings.push({ type: 'dead-link', node: nodeName, msg: `"check ... ${tgt}"  node "${tgt}" does not exist`, target: tgt });
              }
            }
          });
        }

        // takegp/inn ... error goto NODE
        if (cmd === 'takegp' || cmd === 'inn') {
          const errorGotoMatch = t.match(/error\s+goto\s+(\S+)/i);
          if (errorGotoMatch) {
            const tgt = errorGotoMatch[1];
            referencedNodes.add(tgt);
            if (nodeEdges[tgt]) nodeEdges[tgt].inbound.add(nodeName);
            if (nodeEdges[nodeName]) nodeEdges[nodeName].outbound.add(tgt);
            if (!allNodes.has(tgt)) {
              warnings.push({ type: 'dead-link', node: nodeName, msg: `"${cmd} ... error goto ${tgt}"  node "${tgt}" does not exist`, target: tgt });
            }
          }
        }
      }

      if (blockIfDepth > 0) {
        warnings.push({ type: 'unmatched', node: nodeName, msg: `Node "${nodeName}" has ${blockIfDepth} unclosed blockif(s) missing endif` });
      } else if (blockIfDepth < 0) {
        warnings.push({ type: 'unmatched', node: nodeName, msg: `Node "${nodeName}" has ${Math.abs(blockIfDepth)} extra endif(s) without blockif` });
      }
    }

    // Check for orphan nodes (not referenced by any cell, goto, option, or npc)
    for (const name of allNodes) {
      if (name === 'process') continue; // process is special entry point
      if (!referencedNodes.has(name) && !cellRefs.has(name)) {
        warnings.push({ type: 'orphan', node: name, msg: `Node "${name}" is not referenced by any cell, goto, option, or npc` });
      }
    }

    return { warnings, nodeEdges };
  }, [convo, mapGrid]);

  // Get edges for selected node
  const selectedEdges = selectedNode && validation.nodeEdges ? validation.nodeEdges[selectedNode] : null;

  // Determine warning icon per node
  const nodeWarnings = (name) => validation.warnings?.filter(w => w.node === name) || [];

  return (
    <div className="wb-panel wb-convo-panel">
      <div className="wb-convo-header-row">
        <h3>Conversations</h3>
        <span className="wb-convo-header-actions">
          <span className="wb-convo-line-count">{nodeNames.length} node{nodeNames.length !== 1 ? 's' : ''}</span>
          <button
            className={`wb-btn wb-btn-xs ${showRef ? 'active' : ''}`}
            onClick={() => setShowRef(!showRef)}
            title="Command Reference"
          >Ref</button>
        </span>
      </div>

      {/* Search */}
      <div className="wb-convo-search">
        <input
          type="text"
          className="wb-input wb-input-sm wb-convo-search-input"
          placeholder="🔍 Search nodes & content..."
          value={nodeSearch}
          onChange={e => setNodeSearch(e.target.value)}
        />
        {nodeSearch && (
          <button className="wb-convo-search-clear" onClick={() => setNodeSearch('')} title="Clear search">✖</button>
        )}
      </div>

      {/* Validation Warnings */}
      {validation.warnings?.length > 0 && (
        <div className="wb-convo-warnings">
          <details>
            <summary className="wb-convo-warn-summary">
               {validation.warnings.length} warning{validation.warnings.length !== 1 ? 's' : ''}
            </summary>
            <div className="wb-convo-warn-list">
              {validation.warnings.map((w, i) => (
                <div key={i} className={`wb-convo-warn wb-convo-warn-${w.type}`}
                  onClick={() => { if (w.node && convo[w.node]) selectNode(w.node); }}
                  style={{ cursor: convo[w.node] ? 'pointer' : 'default' }}
                >
                  <span className="wb-convo-warn-icon">
                    {w.type === 'dead-link' ? '' : w.type === 'unmatched' ? '' : w.type === 'orphan' ? '' : ''}
                  </span>
                  <span>{w.msg}</span>
                  {w.type === 'dead-link' && w.target && !convo[w.target] && (
                    <button className="wb-btn wb-btn-xs wb-convo-warn-create" onClick={(e) => { e.stopPropagation(); quickCreateNode(w.target); }} title={`Create node "${w.target}"`}>+ Create</button>
                  )}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Node List */}
      <div className="wb-convo-nodes">
        {filteredNodeNames.length === 0 && nodeSearch && (
          <div className="wb-convo-no-results">No nodes match "{nodeSearch}"</div>
        )}
        {filteredNodeNames.map(name => {
          const nw = nodeWarnings(name);
          return (
            <div
              key={name}
              className={`wb-convo-node ${selectedNode === name ? 'selected' : ''} ${nw.length > 0 ? 'has-warning' : ''}`}
              onClick={() => selectNode(name)}
            >
              {renamingNode === name ? (
                <input
                  ref={renameInputRef}
                  className="wb-input wb-input-sm wb-convo-rename-input"
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingNode(null); }}
                  onBlur={commitRename}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <>
                  <span className="wb-convo-node-name">
                    {nw.length > 0 && <span className="wb-convo-node-warn" title={nw.map(w=>w.msg).join('\n')}></span>}
                    {name}
                  </span>
                  <span className="wb-convo-node-actions">
                    <button className="wb-btn wb-btn-xs" onClick={(e) => startRename(name, e)} title="Rename node"></button>
                    <button className="wb-btn wb-btn-xs" onClick={(e) => { e.stopPropagation(); duplicateNode(name); }} title="Duplicate node"></button>
                    <button className="wb-btn wb-btn-xs wb-btn-danger" onClick={(e) => { e.stopPropagation(); deleteNode(name); }}>✖</button>
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="wb-convo-add">
        <input type="text" value={newNodeName} onChange={e => setNewNodeName(e.target.value)} placeholder="New node name" className="wb-input wb-input-sm"
          onKeyDown={e => { if (e.key === 'Enter') addNode(); }} />
        <button className="wb-btn wb-btn-xs wb-btn-success" onClick={addNode}>+</button>
      </div>

      {/* Node Flow Info */}
      {selectedNode && selectedEdges && (selectedEdges.inbound.size > 0 || selectedEdges.outbound.size > 0) && (
        <div className="wb-convo-flow">
          {selectedEdges.inbound.size > 0 && (
            <div className="wb-convo-flow-row">
              <span className="wb-convo-flow-label">In:</span>
              {[...selectedEdges.inbound].map(n => (
                <button key={n} className="wb-convo-flow-chip wb-convo-flow-in" onClick={() => selectNode(n)}>{n}</button>
              ))}
            </div>
          )}
          {selectedEdges.outbound.size > 0 && (
            <div className="wb-convo-flow-row">
              <span className="wb-convo-flow-label">Out:</span>
              {[...selectedEdges.outbound].map(n => (
                <button key={n} className={`wb-convo-flow-chip wb-convo-flow-out ${!convo[n] ? 'missing' : ''}`}
                  onClick={() => { if (convo[n]) selectNode(n); else quickCreateNode(n); }}
                  title={!convo[n] ? `Click to create "${n}"` : `Go to "${n}"`}
                >{n}{!convo[n] && <span className="wb-flow-create-badge">+</span>}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Syntax-highlighted Editor */}
      {selectedNode && (
        <div className="wb-convo-editor">
          <div className="wb-convo-editor-header">
            <strong>{selectedNode}</strong>
            <span className="wb-convo-editor-header-actions">
              <span className="wb-convo-line-count">{editText.split('\n').length} lines</span>
              <button className="wb-btn wb-btn-xs wb-btn-success" onClick={saveNode}>Apply</button>
            </span>
          </div>
          {/* NPC Annotations */}
          {npcLines.length > 0 && (
            <div className="wb-convo-npc-annotations">
              {npcLines.map((npc, i) => (
                <div key={i} className="wb-convo-npc-annotation">
                  <span className="wb-convo-npc-icon"></span>
                  <span className="wb-convo-npc-detail">
                    Line {npc.lineIdx}: <strong>{npc.sprite}</strong> at ({npc.row},{npc.col})  <em
                      className={convo[npc.node] ? 'wb-convo-flow-chip wb-convo-flow-out' : 'wb-convo-flow-chip missing'}
                      onClick={() => { if (convo[npc.node]) selectNode(npc.node); else quickCreateNode(npc.node); }}
                      style={{ cursor: 'pointer', display: 'inline' }}
                    >{npc.node}</em>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="wb-convo-code-wrap">
            {/* Line numbers */}
            <div className="wb-convo-linenums" ref={lineNumRef}>
              {editText.split('\n').map((_, i) => (
                <div key={i} className="wb-convo-linenum">{i}</div>
              ))}
            </div>
            {/* Syntax highlight underlay */}
            <div className="wb-convo-highlight" ref={highlightRef} aria-hidden="true">
              {editText.split('\n').map((line, i) => (
                <div key={i} className="wb-convo-hl-line">
                  {highlightLine(line).map((span, j) => (
                    <span key={j} style={{ color: span.color, fontWeight: span.bold ? 700 : 400 }}>{span.text || ' '}</span>
                  ))}
                </div>
              ))}
            </div>
            {/* Transparent textarea on top */}
            <textarea
              ref={textareaRef}
              value={editText}
              onChange={e => setEditText(e.target.value)}
              onScroll={syncScroll}
              className="wb-convo-textarea-overlay"
              placeholder={`say 'Hello!'\noption nodeB 'Choose this'\ngoto nodeC`}
              spellCheck={false}
              wrap="off"
            />
          </div>
        </div>
      )}

      {/* Command Reference Panel */}
      {showRef && (
        <div className="wb-convo-ref">
          <div className="wb-convo-ref-header">
            <strong>ConvoCode Reference</strong>
            <div className="wb-convo-ref-filters">
              <button className={`wb-btn wb-btn-xs ${refFilter === 'all' ? 'active' : ''}`} onClick={() => setRefFilter('all')}>All</button>
              {CONVO_CMD_CATEGORIES.map(cat => (
                <button key={cat.id} className={`wb-btn wb-btn-xs ${refFilter === cat.id ? 'active' : ''}`}
                  onClick={() => setRefFilter(cat.id)} style={refFilter === cat.id ? { borderColor: cat.color, color: cat.color } : {}}>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          <div className="wb-convo-ref-list">
            {CONVO_COMMANDS.filter(c => refFilter === 'all' || c.cat === refFilter).map(c => (
              <div key={c.cmd} className="wb-convo-ref-item">
                <div className="wb-convo-ref-cmd-row">
                  <code className="wb-convo-ref-cmd" style={{ color: CMD_COLORS[c.cmd.toLowerCase()] || '#d4d0c8' }}>{c.cmd}</code>
                  {selectedNode && (
                    <button className="wb-btn wb-btn-xs" onClick={() => insertSnippet(c.template)} title="Insert snippet">+</button>
                  )}
                </div>
                <div className="wb-convo-ref-syntax">{c.syntax}</div>
                <div className="wb-convo-ref-desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAP PROPERTIES PANEL
// ============================================================
const MapPropsPanel = ({ mapState, onChange, onResize }) => {
  const [newRows, setNewRows] = useState(mapState.rows);
  const [newCols, setNewCols] = useState(mapState.cols);

  return (
    <div className="wb-panel">
      <h3>Map Properties</h3>
      <div className="wb-form-row">
        <label>Filename:</label>
        <span className="wb-prop-value">{mapState.filename}</span>
      </div>
      <div className="wb-form-row">
        <label>Size:</label>
        <span className="wb-prop-value">{mapState.rows}  {mapState.cols}</span>
      </div>
      <div className="wb-form-row">
        <label>Resize:</label>
        <input type="number" value={newRows} onChange={e => setNewRows(parseInt(e.target.value) || 1)} min="1" max="100" className="wb-input wb-input-xs" style={{width: 50}} />
        <span></span>
        <input type="number" value={newCols} onChange={e => setNewCols(parseInt(e.target.value) || 1)} min="1" max="100" className="wb-input wb-input-xs" style={{width: 50}} />
        <button className="wb-btn wb-btn-xs wb-btn-success" onClick={() => onResize(newRows, newCols)}
          disabled={newRows === mapState.rows && newCols === mapState.cols}
          title="Apply resize">Apply</button>
      </div>
      <p className="wb-panel-hint">Shrinking trims from bottom/right. Growing adds default tiles.</p>
      <div className="wb-form-row">
        <label>Music:</label>
        <input
          type="text"
          value={mapState.music || ''}
          onChange={e => onChange({ music: e.target.value })}
          placeholder="e.g., plains.mid"
          className="wb-input"
        />
      </div>
      <div className="wb-form-row">
        <label>Monsters:</label>
        <span className="wb-prop-value">{Object.keys(mapState.monsters || {}).length} entries</span>
      </div>
      <div className="wb-form-row">
        <label>Convo Nodes:</label>
        <span className="wb-prop-value">{Object.keys(mapState.convo || {}).length}</span>
      </div>
    </div>
  );
};

export default WorldBuilder;
